import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

const MAX_SECONDS = 60;

/**
 * VoiceCapture — record a manuscript passage on the phone and get it back as text.
 * Emits the transcribed text via `onTranscribed(text)` so the parent can append it
 * to whichever field the user was writing into.
 *
 * The record button doubles as a 60-second countdown ring — the border fills as time
 * elapses so authors never accidentally overrun Whisper's 25MB / ~10-minute cap.
 */
export default function VoiceCapture({ onTranscribed, label = "Dictate", testid = "voice-capture" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const autoStopRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
  }, []);

  const drawWave = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width, h = canvas.height;
    const buf = new Uint8Array(analyser.fftSize);
    const render = () => {
      analyser.getByteTimeDomainData(buf);
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = "#E6B860";
      ctx.beginPath();
      const step = w / buf.length;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i] / 128.0; // 0-2, midline at 1
        const y = (v * h) / 2;
        const x = i * step;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      rafRef.current = requestAnimationFrame(render);
    };
    render();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
        analyserRef.current = null;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 500) {
          toast.error("Recording too short — try again.");
          setState("idle");
          return;
        }
        setState("transcribing");
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice." + (rec.mimeType?.includes("webm") ? "webm" : "ogg"));
          const { data } = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text?.trim()) {
            onTranscribed(data.text.trim());
            toast.success("Transcribed.");
          } else {
            toast.error("Nothing was heard.");
          }
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          setState("idle");
        }
      };
      rec.start();
      mediaRef.current = rec;
      // Set up Web Audio analyser for waveform
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        setTimeout(drawWave, 50);
      } catch (err) {
        // Waveform is decorative — fall back silently if Web Audio isn't available
        console.warn("Waveform analyser unavailable", err);
      }
      setState("recording");
      setSeconds(0);
      tickRef.current = setInterval(() => setSeconds((s) => Math.min(s + 1, MAX_SECONDS)), 1000);
      autoStopRef.current = setTimeout(() => {
        toast.info("60-second cap reached — transcribing what you've said.");
        stop();
      }, MAX_SECONDS * 1000);
    } catch (e) {
      toast.error("Microphone permission denied.");
    }
  };

  const stop = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const remaining = MAX_SECONDS - seconds;
  const isRecording = state === "recording";
  const isTranscribing = state === "transcribing";
  // SVG ring math
  const R = 10;
  const C = 2 * Math.PI * R;
  const progress = seconds / MAX_SECONDS;
  const dashOffset = C * (1 - progress);

  return (
    <button
      type="button"
      onClick={isRecording ? stop : (state === "idle" ? start : null)}
      disabled={isTranscribing}
      className={`relative inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-sm font-mono-brass text-[10px] uppercase tracking-widest transition-colors duration-200 ${
        isRecording
          ? "bg-[rgba(140,58,52,0.25)] border border-[#8C3A34] text-[#F7F3EC]"
          : "border border-[rgba(217,180,114,0.3)] text-[#D9B472] hover:border-[#D9B472] hover:bg-[rgba(217,180,114,0.06)]"
      }`}
      data-testid={testid}
      aria-label={isRecording ? `Stop recording, ${remaining} seconds left` : label}
    >
      <span className="relative inline-flex items-center justify-center w-6 h-6" data-testid={`${testid}-ring`}>
        <svg width="24" height="24" viewBox="0 0 24 24" className="absolute inset-0 -rotate-90">
          <circle cx="12" cy="12" r={R} fill="none" stroke="rgba(217,180,114,0.18)" strokeWidth="2" />
          {isRecording && (
            <circle
              cx="12" cy="12" r={R} fill="none"
              stroke={remaining <= 10 ? "#E9B8A8" : "#E6B860"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 1s linear, stroke 300ms ease" }}
            />
          )}
        </svg>
        {isRecording ? <Square className="w-3 h-3 relative z-10" /> :
         isTranscribing ? <Loader2 className="w-3 h-3 animate-spin relative z-10" /> :
         <Mic className="w-3 h-3 relative z-10" />}
      </span>
      {isRecording ? <span className={remaining <= 10 ? "text-[#E9B8A8]" : ""} data-testid={`${testid}-countdown`}>{remaining}s</span> :
       isTranscribing ? "Transcribing…" :
       label}
      {isRecording && (
        <canvas
          ref={canvasRef}
          width={72}
          height={20}
          className="ml-1"
          data-testid={`${testid}-waveform`}
          aria-hidden="true"
        />
      )}
    </button>
  );
}
